import mapboxgl from 'mapbox-gl';
import { MAPBOX_ACCESS_TOKEN, validateMapboxEnvironment } from './mapbox';

/**
 * Mapbox configuration utility for managing telemetry and error handling
 */

// Use official Mapbox navigation style to avoid private-style access issues
export const CUSTOM_NAVIGATION_STYLE = 'mapbox://styles/mapbox/navigation-day-v1';

// Simple transform request function for basic URL handling
export const createMapboxTransformRequest = (): mapboxgl.TransformRequestFunction => {
  return (url: string, resourceType?: string) => {
    // Allow all requests to proceed normally
    // CSP headers now handle blocking unwanted features
    return { url };
  };
};

// Default map options that prevent telemetry issues
export const getOptimizedMapboxOptions = (
  container: string | HTMLElement,
  style: string = CUSTOM_NAVIGATION_STYLE, // Use custom navigation style as default
  options: Partial<mapboxgl.MapboxOptions> = {}
): mapboxgl.MapboxOptions => {
  return {
    container,
    style,
    // Disable telemetry collection
    collectResourceTiming: false,
    // Use transform request to block analytics
    transformRequest: createMapboxTransformRequest(),
    // Reduce network requests
    maxTileCacheSize: 200,
    // Optimize for better performance
    antialias: false,
    // Keep attribution but reduce visual noise
    attributionControl: false,
    ...options
  };
};

// Global error handler for Mapbox maps
export const setupMapboxErrorHandling = (map: mapboxgl.Map): void => {
  map.on('error', (e) => {
    const error = e.error;
    
    // Silently ignore telemetry-related errors
    if (error?.message?.includes('events.mapbox.com') || 
        error?.message?.includes('ERR_BLOCKED_BY_CLIENT') ||
        error?.message?.includes('analytics') ||
        error?.message?.includes('telemetry') ||
        error?.name === 'NetworkError') {
      return; // Silently ignore these errors
    }
    
    // Log other errors as warnings (not errors to reduce console noise)
    console.warn('Mapbox map error:', error);
  });

  // Handle source errors
  map.on('sourcedata', (e) => {
    if (e.isSourceLoaded && e.sourceDataType === 'metadata') {
      const source = map.getSource(e.sourceId);
      // Handle source loading issues gracefully
    }
  });
};

// Utility to check if Mapbox is properly configured with fallback support
export const isMapboxConfigured = (): boolean => {
  // Use the consistent token (includes fallback)
  const token = MAPBOX_ACCESS_TOKEN;
  
  if (!token || token === 'your_mapbox_access_token') {
    console.error('Mapbox access token is not properly configured.');
    return false;
  }
  
  // Validate WebGL support
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (!gl) {
      console.warn('WebGL not supported - maps may not work properly');
      return false;
    }
  } catch (error) {
    console.warn('WebGL support check failed:', error);
    return false;
  }
  
  return true;
};

// Enhanced Mapbox initialization with better error handling
export const initializeMapbox = (): boolean => {
  try {
    // Validate environment first
    const hasValidEnv = validateMapboxEnvironment();
    
    if (!isMapboxConfigured()) {
      console.warn('Mapbox configuration check failed - using fallback initialization');
    }
    
    // Set global access token (uses fallback if needed)
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
    
    // Optimize global settings
    if (typeof mapboxgl.maxParallelImageRequests !== 'undefined') {
      mapboxgl.maxParallelImageRequests = 8; // Reduce from default 16
    }
    
    // Only log version in development
    if (import.meta.env.DEV) {
      console.log(`Mapbox GL JS v${mapboxgl.version} initialized ${hasValidEnv ? 'with custom token' : 'with fallback token'}`);
    }
    
    return true;
  } catch (error) {
    console.error('Failed to initialize Mapbox:', error);
    return false;
  }
};

// Enhanced fetch wrapper for Mapbox API calls with timeout and error handling
export const fetchMapboxAPI = async (
  url: string, 
  options: RequestInit = {},
  timeout: number = 5000
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        'Accept': 'application/json',
        ...options.headers
      }
    });
    
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

// Export types for better TypeScript support
export interface MapboxErrorHandler {
  onError?: (error: Error) => void;
  onNetworkError?: (error: Error) => void;
  onTelemetryBlock?: () => void;
} 